import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { find } from "lodash-es";
import { Observable, lastValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  RateTable,
  RateTableCreate,
  RateTableLight,
  RateTableUpdate
} from "../../models/rate-table/rate-table";
import { omitFalsyProperties } from "src/app/support/object";
@Injectable()
export class RateTableBackend {
  private readonly http = inject(HttpClient);

  list(request: PageRequest): Observable<PaginatedResult<RateTable>> {
    return this.http.get<PaginatedResult<RateTable>>(
      `${environment.snoots.api.healthcare}/rate-table`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          ...request.filterValues
        }
      }
    );
  }

  listAll(): Observable<RateTable[]> {
    return this.list({ page: 1, pageSize: 1000 }).pipe(
      map((result) => result.docs)
    );
  }

  listAllLight(): Observable<RateTableLight[]> {
    return this.list({
      page: 1,
      pageSize: 1000,
      filterValues: {
        light: true
      }
    }).pipe(map((result) => result.docs));
  }

  listAllEnabled(): Observable<RateTable[]> {
    return this.listAll().pipe(
      map((rateTables) => rateTables.filter((x) => !!x.enabled))
    );
  }

  listAllEnabledLight(): Observable<RateTableLight[]> {
    return this.listAllLight().pipe(
      map((rateTables) => rateTables.filter((x) => !!x.enabled))
    );
  }

  listEnabledLight(
    request: PageRequest,
    name?: string
  ): Observable<RateTableLight[]> {
    return this.http
      .get<PaginatedResult<RateTableLight>>(
        `${environment.snoots.api.healthcare}/rate-table`,
        {
          params: omitFalsyProperties({
            p: request.page,
            s: request.pageSize,
            light: true,
            name
          })
        }
      )
      .pipe(map((result) => result.docs))
      .pipe(map((rateTables) => rateTables.filter((x) => !!x.enabled)));
  }

  load(rateTableId: string): Observable<RateTable> {
    return this.http.get<RateTable>(
      `${environment.snoots.api.healthcare}/rate-table/${rateTableId}`
    );
  }

  loadLight(id: string): Observable<RateTableLight> {
    return this.http.get<RateTable>(
      `${environment.snoots.api.healthcare}/rate-table/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  loadDefault(): Observable<RateTableLight | undefined> {
    return this.listAllLight().pipe(
      map((rateTables) => find(rateTables, (rateTable) => rateTable.default))
    );
  }

  @UserNotify({
    successMessage: marker("Tariffario creato"),
    errorMessage: marker("Impossibile creare il tariffario")
  })
  create(request: RateTableCreate): Observable<RateTable> {
    return this.http.post<RateTable>(
      `${environment.snoots.api.healthcare}/rate-table`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Tariffario aggiornato"),
    errorMessage: marker("Impossibile aggiornare il tariffario")
  })
  update(id: string, request: RateTableUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/rate-table/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Tariffario eliminato"),
    errorMessage: marker("Impossibile eliminare il tariffario")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/rate-table/${id}`
    );
  }

  async isAvailable(rateTableId: string): Promise<boolean> {
    if (!rateTableId) {
      return true;
    }

    const rateTable = await lastValueFrom(this.loadLight(rateTableId));

    if (rateTable.deletedAt) {
      return false;
    }

    return true;
  }
}
